import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "hiddenId", "hint"]
  static values = {
    options: Array,
    newGroupText: { type: String, default: "New group will be created" },
    existingGroupText: { type: String, default: "Existing group" }
  }

  connect() {
    this.selectedIndex = -1
    document.addEventListener("click", this.handleClickOutside.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()

    if (query === "") {
      this.hideDropdown()
      this.hiddenIdTarget.value = ""
      this.hideHint()
      return
    }

    const matches = this.optionsValue.filter(option =>
      option.name.toLowerCase().includes(query)
    )

    const exactMatch = this.optionsValue.find(option =>
      option.name.toLowerCase() === query
    )

    this.renderDropdown(matches, query)
    this.updateHint(exactMatch, query)
  }

  renderDropdown(matches, query) {
    if (matches.length === 0 && query === "") {
      this.hideDropdown()
      return
    }

    let html = ""

    matches.forEach((option, index) => {
      const highlightedName = this.highlightMatch(option.name, query)
      html += `<div class="px-4 py-2 cursor-pointer hover:bg-base-200 ${index === this.selectedIndex ? 'bg-base-200' : ''}"
                   data-action="click->combobox#select"
                   data-id="${option.id}"
                   data-name="${option.name}">${highlightedName}</div>`
    })

    this.dropdownTarget.innerHTML = html
    this.showDropdown()
  }

  highlightMatch(text, query) {
    if (!query) return text
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi')
    return text.replace(regex, '<strong class="text-primary">$1</strong>')
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  updateHint(exactMatch, query) {
    if (!query) {
      this.hideHint()
      this.hiddenIdTarget.value = ""
      return
    }

    if (exactMatch) {
      this.hintTarget.textContent = this.existingGroupTextValue
      this.hintTarget.classList.remove("text-success")
      this.hintTarget.classList.add("text-info")
      this.hiddenIdTarget.value = exactMatch.id
    } else {
      this.hintTarget.textContent = `"${this.inputTarget.value.trim()}" — ${this.newGroupTextValue}`
      this.hintTarget.classList.remove("text-info")
      this.hintTarget.classList.add("text-success")
      this.hiddenIdTarget.value = ""
    }
    this.hintTarget.classList.remove("hidden")
  }

  hideHint() {
    this.hintTarget.classList.add("hidden")
  }

  select(event) {
    const id = event.currentTarget.dataset.id
    const name = event.currentTarget.dataset.name

    this.inputTarget.value = name
    this.hiddenIdTarget.value = id
    this.hintTarget.textContent = this.existingGroupTextValue
    this.hintTarget.classList.remove("text-success", "hidden")
    this.hintTarget.classList.add("text-info")
    this.hideDropdown()
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden")
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.selectedIndex = -1
  }

  handleKeydown(event) {
    const items = this.dropdownTarget.querySelectorAll("[data-action]")

    switch(event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.updateSelection(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          items[this.selectedIndex].click()
        }
        break
      case "Escape":
        this.hideDropdown()
        break
    }
  }

  updateSelection(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-base-200")
      } else {
        item.classList.remove("bg-base-200")
      }
    })
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenIdTarget.value = ""
    this.hideHint()
    this.hideDropdown()
  }
}
